import { useEffect, useState, forwardRef, useImperativeHandle } from 'react';
import { AccountBalance } from '../../model/Account-balance.model';
import AccountBalanceService from '../../service/account-balance-service/Account-balance.service';
import { Card, Typography, Button, Tooltip, Row, Col, Modal } from 'antd';
import { PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import AddMoneyModal from './AddMoneyModal';

interface MainAccBalanceProps {
  onAddMoneySuccess?: () => void;
}

const MainAccBalance = forwardRef<
  { fetchBalance: () => void },
  MainAccBalanceProps
>(({ onAddMoneySuccess }, ref) => {
  const [balance, setBalance] = useState<AccountBalance>();
  const [isModalVisible, setIsModalVisible] = useState(false);

  const fetchBalance = async () => {
    try {
      const data = await AccountBalanceService.getAccountBalance();
      setBalance(data);
    } catch (error) {
      console.error('Error fetching balance:', error);
    }
  };

  useEffect(() => {
    fetchBalance();
  }, []);

  useImperativeHandle(ref, () => ({
    fetchBalance,
  }));

  const handleAddMoneySuccess = () => {
    fetchBalance();
    onAddMoneySuccess?.();
  };

  const handleResetBalance = () => {
    Modal.confirm({
      title: 'Xác nhận reset số dư',
      content:
        'Bạn có chắc chắn muốn reset số dư tài khoản về 0? Hành động này không thể hoàn tác.',
      okText: 'Xác nhận',
      cancelText: 'Hủy',
      okType: 'danger',
      onOk: async () => {
        try {
          await AccountBalanceService.resetBalance();
          fetchBalance();
          onAddMoneySuccess?.();
          Modal.success({
            title: 'Thành công',
            content: 'Đã reset số dư tài khoản về 0.',
          });
        } catch (error) {
          console.error('Error resetting balance:', error);
          Modal.error({
            title: 'Lỗi',
            content: 'Có lỗi xảy ra khi reset số dư tài khoản.',
          });
        }
      },
    });
  };

  return (
    <Row gutter={[16, 16]}>
      <Col xs={24} sm={24} md={24} lg={24} xl={24}>
        <Card
          title={
            <div className="flex justify-between items-center flex-wrap">
              <Typography.Title level={4} className="!mb-0">
                Số dư tài khoản
              </Typography.Title>
              <div className="flex gap-2">
                <Tooltip title="Nạp tiền vào tài khoản">
                  <Button
                    type="default"
                    icon={<PlusOutlined />}
                    onClick={() => setIsModalVisible(true)}
                  />
                </Tooltip>
                <Tooltip title="Reset số dư về 0">
                  <Button
                    type="default"
                    danger
                    icon={<ReloadOutlined />}
                    onClick={handleResetBalance}
                  />
                </Tooltip>
              </div>
            </div>
          }
          className="w-full"
        >
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2">
            <Typography.Text>Số dư:</Typography.Text>
            <Typography.Text strong className="text-green-600 text-lg">
              {balance
                ? new Intl.NumberFormat('vi-VN', {
                    style: 'currency',
                    currency: 'VND',
                  }).format(balance.balanceAmount)
                : '...'}
            </Typography.Text>
          </div>
        </Card>
      </Col>

      <AddMoneyModal
        visible={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        onSuccess={handleAddMoneySuccess}
        balanceId={balance?.id || 0}
      />
    </Row>
  );
});

export default MainAccBalance;
